import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import api from "@/utils/api";
import { tr } from "@/utils/i18n";
import {
  ArrowLeft,
  Beaker,
  Calendar,
  Clock,
  DollarSign,
  Download,
  Eye,
  Package,
  Share2,
  Sparkles,
  User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SharedCyclePage() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSharedCycle = useCallback(async () => {
    try {
      const response = await api.get(`/shared/${shareToken}`);
      setCycle(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Shared cycle not found");
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => {
    fetchSharedCycle();
  }, [fetchSharedCycle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Card className="bg-zinc-950 border-zinc-800 max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              {tr(language, "Cycle Not Found", "Ciclo Não Encontrado", "Ciclo No Encontrado")}
            </h2>
            <p className="text-zinc-400 mb-6">{error}</p>
            <Button onClick={() => navigate("/")} className="btn-primary">
              {tr(language, "Go to Home", "Ir para Início", "Ir al Inicio")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grid-pattern">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="shared-back-home"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-white" data-testid="shared-cycle-title">
                    {tr(language, "Shared Cycle", "Ciclo Compartilhado", "Ciclo Compartido")}
                  </h1>
                  <Badge variant="outline" className="border-cyan-500/50 text-cyan-400">
                    <Eye className="w-3 h-3 mr-1" />
                    {cycle.viewer_permission === "edit"
                      ? tr(language, "Edit", "Edição", "Edición")
                      : tr(language, "View Only", "Visualização", "Vista")}
                  </Badge>
                </div>
                <p className="text-sm text-zinc-400" data-testid="shared-cycle-goal">{cycle.goal}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary */}
        <Card className="bg-gradient-to-br from-zinc-900 to-black border-cyan-500/20 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white mb-2">
                  {tr(language, "Cycle Summary", "Resumo do Ciclo", "Resumen del Ciclo")}
                </h2>
                <p className="text-zinc-400" data-testid="shared-cycle-summary">{cycle.summary}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Beaker className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{cycle.peptides?.length || 0}</p>
                  <p className="text-xs text-zinc-500">Peptides</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{cycle.total_duration_weeks}</p>
                  <p className="text-xs text-zinc-500">{t("weeks")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Package className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {cycle.peptides?.reduce((acc, p) => acc + p.vials_needed, 0) || 0}
                  </p>
                  <p className="text-xs text-zinc-500">{t("vials")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${cycle.total_cost_usd}</p>
                  <p className="text-xs text-zinc-500">{t("totalCost")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Peptides List */}
        <h3 className="text-xl font-semibold text-white mb-4">{t("peptideProtocol")}</h3>
        <div className="space-y-4 mb-8">
          {cycle.peptides?.map((peptide, index) => (
            <Card key={index} className="bg-zinc-950/50 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{peptide.name}</h4>
                    <p className="text-sm text-zinc-400">{peptide.description}</p>
                  </div>
                  <Badge className="bg-cyan-500/20 text-cyan-400">
                    {peptide.dosage_mg ? `${peptide.dosage_mg}mg` : `${peptide.dosage_iu}IU`} {t("perDay")}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">{t("timing")}:</span>
                    <span className="text-white ml-2">{peptide.timing}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">{t("duration")}:</span>
                    <span className="text-white ml-2">{peptide.cycle_duration_weeks} {t("weeks")}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">{t("vials")}:</span>
                    <span className="text-white ml-2">{peptide.vials_needed}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">{t("totalCost")}:</span>
                    <span className="text-green-400 ml-2">${peptide.total_cost_usd}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border-cyan-500/30">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-bold text-white mb-2">
              {tr(language, "Create Your Own Cycle", "Crie Seu Próprio Ciclo", "Crea Tu Propio Ciclo")}
            </h3>
            <p className="text-zinc-400 mb-6">
              {tr(language, "Start free and get personalized peptide recommendations.", "Comece gratuitamente e receba recomendações personalizadas de peptídeos.", "Comienza gratis y recibe recomendaciones personalizadas de péptidos.")}
            </p>
            <Button onClick={() => navigate("/")} className="btn-primary">
              {tr(language, "Get Started", "Começar Agora", "Empezar Ahora")}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
